'use client'

/**
 * ServiceCard — beige card with an animated graphic preview at the top, a
 * brand-coloured uppercase category, the service title, and a flexible
 * row of pill-style tags. Designed to sit in a responsive grid (1 / 2 / 4
 * columns) on the home page Services section.
 *
 * The card is a real anchor — the whole card is clickable. Hover lifts the
 * card slightly and brightens the title, matching the rest of the site's
 * subtle interaction language.
 */

import Link from 'next/link'
import { SERVICE_GRAPHICS, type ServiceGraphicKey } from '@/components/ServiceGraphics'

interface ServiceCardProps {
  category: string
  title: string
  tags: string[]
  href: string
  graphic: ServiceGraphicKey
}

export default function ServiceCard({
  category,
  title,
  tags,
  href,
  graphic,
}: ServiceCardProps) {
  const Graphic = SERVICE_GRAPHICS[graphic]

  return (
    <div className="h-full">
      <Link
        href={href}
        className="group relative block h-full rounded-2xl bg-[#eeebf3] p-4 md:p-5 after:hidden transition-colors duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[#ece8f4]"
      >
        {/* Animated graphic */}
        <div className="mb-5 md:mb-6">
          <Graphic />
        </div>

        {/* Category */}
        <p className="text-[11px] md:text-xs tracking-[0.22em] uppercase text-brand-500 mb-2.5 font-medium">
          {category}
        </p>

        {/* Title */}
        <h3 className="text-lg md:text-xl text-charcoal-800 mb-4 leading-tight transition-colors duration-300 group-hover:text-brand-600">
          {title}
        </h3>

        {/* Tag pills */}
        <div className="flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-2.5 py-1 rounded-full bg-white text-[11px] md:text-xs text-charcoal-600 border border-charcoal-900/[0.06] whitespace-nowrap"
            >
              {tag}
            </span>
          ))}
        </div>
      </Link>
    </div>
  )
}
